import { useCallback } from 'react';
import { useProfileQuery } from '@/features/settings/queries';
import { roleCan, type Access } from './access';

/**
 * Role half of the two-guard model: may the caller's role in the CURRENT
 * workspace (`profile.role` — the same value the JWT carries) do `access`?
 *
 * `isResolved` is false until the profile lands; `allowed` is false until then
 * too, so a gated control is never optimistically revealed. Pass no `access` for
 * a control with no role requirement (always allowed, always resolved) — that lets
 * a component take an optional `access` prop and call this unconditionally.
 */
export function useRoleAccess(access?: Access | null): { allowed: boolean; isResolved: boolean } {
  const query = useProfileQuery({ enabled: access != null });
  if (!access) return { allowed: true, isResolved: true };
  const isResolved = query.isSuccess;
  return { allowed: isResolved && roleCan(query.data?.role, access), isResolved };
}

/**
 * List-filter form of `useRoleAccess` for surfaces that gate many entries at once
 * (nav, command palette, settings rail). `can(undefined)` is true; every gated
 * entry is false until the profile resolves, so nothing role-blocked flashes in.
 */
export function useRoleCan(): { can: (access?: Access | null) => boolean; isResolved: boolean } {
  const query = useProfileQuery();
  const role = query.data?.role;
  const isResolved = query.isSuccess;
  // Stable per (resolved, role) so callers can list it as a memo dependency.
  const can = useCallback(
    (access?: Access | null) => !access || (isResolved && roleCan(role, access)),
    [isResolved, role],
  );
  return { can, isResolved };
}
